// ── Viewed listings (buyer-facing "Viewed" marker) ──────────────────────────
// Device-local record of listings the buyer has opened and lingered on (the
// 3-second linger is enforced by the listing-page hook, not here). Mirrors the
// tphRecentSearches convention in util/searchSubmit.js: all access is
// SSR-guarded and try/catch-wrapped (private-mode browsers throw) — the viewed
// record is strictly best-effort and must never break a page render.
//
// Entry shape: { id: string, viewedAt: number (epoch ms) }.
// Records fade after VIEWED_TTL_MS (treated as not-viewed and pruned on read).
// Store is capped at VIEWED_CAP entries; the oldest drop first when full.
// No cross-device sync in v1.

const VIEWED_LISTINGS_KEY = 'tphViewedListings';

// 90 days. Records older than this are treated as not-viewed and pruned on read.
export const VIEWED_TTL_MS = 90 * 24 * 60 * 60 * 1000;

// Hard cap on stored entries; oldest (by viewedAt) drop first when full.
export const VIEWED_CAP = 500;

// Read + parse the raw store, returning only well-formed, non-expired entries
// (most-recent first). Shared internal helper — never throws.
const readValidEntries = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(VIEWED_LISTINGS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - VIEWED_TTL_MS;
    return parsed
      .filter(
        e =>
          e &&
          typeof e.id === 'string' &&
          e.id &&
          typeof e.viewedAt === 'number' &&
          e.viewedAt > cutoff
      )
      .sort((a, b) => b.viewedAt - a.viewedAt);
  } catch (e) {
    return [];
  }
};

/**
 * Currently-valid viewed listing ids, most-recent first. Entries older than
 * VIEWED_TTL_MS are excluded. SSR-safe (returns [] on the server) and never
 * throws.
 *
 * @returns {string[]} listing uuids
 */
export const getViewedListings = () => readValidEntries().map(e => e.id);

/**
 * Mark a listing as viewed now: upsert { id, viewedAt: Date.now() }, moving an
 * existing entry to the front. Prunes expired entries and enforces the
 * VIEWED_CAP (oldest drop first). Best-effort — no-ops when storage is
 * unavailable and never throws.
 *
 * @param {string} id listing uuid
 */
export const recordViewedListing = id => {
  if (typeof id !== 'string' || !id || typeof window === 'undefined') return;
  try {
    const rest = readValidEntries().filter(e => e.id !== id);
    // Newest first; sorted reads/prunes mean slice(0, CAP) drops the oldest.
    const next = [{ id, viewedAt: Date.now() }, ...rest].slice(0, VIEWED_CAP);
    window.localStorage.setItem(VIEWED_LISTINGS_KEY, JSON.stringify(next));
  } catch (e) {
    // localStorage unavailable — skip silently.
  }
};
